// Schedules a MIDI song's notes on the audio clock (issue #3718): the Player's Web Audio path, with
// the Web Audio itself behind the SongSink so the rules here run under a fake clock in unit tests.
//
// The song is a timeline in "file seconds" (midi_notes.ts: every note's start and length at the
// file's own tempo map). Playback maps file seconds onto the audio clock through an anchor and a
// rate: rate is the tempo the listener chose over the file's tempo, so file seconds pass `rate`
// times as fast as audio seconds. Tempo is therefore a scheduling parameter and nothing else: a
// change re-anchors at the clock's reading now and schedules what is still to come at the new
// rate, the notes already sounding play on, and the position never jumps. Part volumes are not
// this module's business at all (the sink mixes each voice through a gain of its own), so a mixer
// change interrupts nothing.
//
// Looking ahead. Everything audible is scheduled on the audio clock, never from a timer, because a
// timer behind a locked iPhone is not something the measurements cover (docs/learn/audio-research.md,
// 2026-10-01: scheduled-ahead notes kept playing with the phone locked, "no timers involved"). The
// scheduler keeps `lookaheadSec` of audio scheduled ahead of the clock and asks the sink to wake it
// (`at`, a callback the sink fires from the audio clock, not from a timer) when the scheduled stretch
// has shrunk to `refillSec`; the player also calls fill() from a timer as a backstop, which costs
// nothing when there is nothing to add. A whole song is never scheduled at once: a hymn is a few
// hundred to a thousand notes, and every one of them would sit in the audio graph being pulled
// each render quantum, which an old phone would feel.
//
// Verses. The song loops for as long as `loop` is true, as the MP3 path's looping audio element
// did, but with a rest between verses (issue #3843) so a congregation has time to breathe: on the
// timeline a verse is its notes and then `verseGapBeats` beats of silence, the beat as the song is
// counted (a half note in 2/2, a quarter in 3/4: `beatQuarterNotes`) at the tempo the song ends
// in. The rest is in file time, so a slowed-down song gets a proportionally longer breath. With
// `holdFinalNotes` the rest is not silent: every note that ends with the verse is held through it,
// a fermata on the final chord, and the next verse follows as the chord releases.
// The stretch scheduled ahead runs across the rest and the boundary after it, and `onVerseEnd` is
// called when the clock passes that boundary, as the next verse begins (the player's endOfFile,
// which is where the Sequencer counts verses and decides whether the next one loops). Through the
// rest the position holds at the end of the verse. With `loop` false scheduling stops at the end of
// the verse's notes, with no rest after them, the position reports the end, and `onVerseEnd` is
// called there; what happens next (a restart, the next song, a stop) is the Sequencer's call.
// Setting `loop` while playing re-schedules the stretch ahead under the new rule, so a last verse
// that is announced at the boundary never runs into a verse that was not asked for.

import { MidiNote, MidiSong } from './midi_notes.ts';

// A note the sink has scheduled: cancelled before its start it never sounds, cancelled while it
// sounds it fades out at once
export interface ScheduledNoteHandle {
  cancel(): void;
}

export interface SongSink {
  // The audio clock, in seconds
  now(): number;
  // Sounds `note` from `whenSec` on the audio clock for `durationSec` of audio time
  play(note: MidiNote, whenSec: number, durationSec: number): ScheduledNoteHandle;
  // Calls `callback` once the audio clock has passed `whenSec`; returns the cancel
  at(whenSec: number, callback: () => void): () => void;
}

export interface WebAudioSongSchedulerOptions {
  lookaheadSec?: number; // how much audio is kept scheduled ahead of the clock
  refillSec?: number; // the scheduled stretch at which the next fill is asked for
  leadSec?: number; // how far ahead of the clock playback starts, so the first notes are scheduled in time
  verseGapBeats?: number; // the rest between verses, in beats as the song is counted, at the tempo it ends in
  holdFinalNotes?: boolean; // hold the notes that end with the verse through the rest, rather than leave it silent
}

const defaultLookaheadSec = 20;
const defaultRefillSec = 10;
const defaultLeadSec = 0.1;
// The rest between verses (issue #3843), still being tuned on real songs: three quarters of a felt
// beat, the organist's breath before the pickup (a whole beat was a little long on most songs)
const defaultVerseGapBeats = 0.75;
const defaultHoldFinalNotes = true;
// A note ending this close to the verse's end ends with it (the ends come from tick arithmetic)
const verseEndEpsilonSec = 0.001;
// Where the verse boundary is judged from inside the callback that announces it (see setLoop): the
// clock is read a little ahead, so a reading a render quantum short of the boundary still counts
// as the new verse
const boundaryEpsilonSec = 0.02;

interface ScheduledEntry {
  handle: ScheduledNoteHandle;
  startSec: number; // on the audio clock
  endSec: number; // on the audio clock; forgotten after this
}

export interface NowPlayingPosition {
  durationSec: number;
  positionSec: number;
}

export class WebAudioSongScheduler {
  private readonly sink: SongSink;
  private readonly lookaheadSec: number;
  private readonly refillSec: number;
  private readonly leadSec: number;
  private readonly verseGapBeats: number;
  private readonly holdFinalNotes: boolean;
  private song: MidiSong | undefined;
  private rate = 1; // file seconds per audio second
  private loop = false;
  private playing = false;
  private positionWhilePaused = 0; // file seconds into the verse; past its notes when paused in the rest
  // The mapping from the timeline (file seconds since this play began, verses counted on) to the
  // audio clock: timelineSec = anchorTimelineSec + (audioSec - anchorAudioSec) * rate
  private anchorAudioSec = 0;
  private anchorTimelineSec = 0;
  // How far the timeline has been scheduled: the verse (0 for the one the play began in) and the
  // file seconds into it, with the index of the next note of that verse to schedule
  private cursorVerse = 0;
  private cursorOffsetSec = 0;
  private cursorNoteIndex = 0;
  // Scheduling stopped at the end of a verse with loop off: the timeline ends there
  private endTimelineSec: number | undefined;
  private scheduled: ScheduledEntry[] = [];
  private cancelWakes: Array<() => void> = []; // the verse-end announcements still to come
  // The one refill wake: re-armed only when the cursor has moved, so a fill that adds nothing (the
  // player's backstop timer, every second) leaves the graph as it is
  private cancelRefillWake: (() => void) | undefined;
  private refillWakeCursorSec: number | undefined;
  // Called when the clock passes the end of a verse, looping or not
  onVerseEnd: (() => void) | undefined;
  // Inside the onVerseEnd call: the clock may read a hair short of the boundary just announced,
  // and the position is judged a little ahead so it reads as the new verse's, not the rest's end
  private announcingVerseEnd = false;

  constructor(sink: SongSink, options: WebAudioSongSchedulerOptions = {}) {
    this.sink = sink;
    this.lookaheadSec = options.lookaheadSec ?? defaultLookaheadSec;
    // the refill point lies inside the lookahead, or a fill would ask to be woken at once
    this.refillSec = Math.min(options.refillSec ?? defaultRefillSec, this.lookaheadSec / 2);
    this.leadSec = options.leadSec ?? defaultLeadSec;
    this.verseGapBeats = options.verseGapBeats ?? defaultVerseGapBeats;
    this.holdFinalNotes = options.holdFinalNotes ?? defaultHoldFinalNotes;
  }

  // A new song (or none) starts stopped at its beginning
  setSong(song: MidiSong | undefined) {
    this.pause();
    this.song = song;
    this.positionWhilePaused = 0;
  }

  get isPlaying(): boolean {
    return this.playing;
  }

  get isLooping(): boolean {
    return this.loop;
  }

  // The song's length in audio seconds at the current rate
  get durationSec(): number | undefined {
    if (!this.song) {
      return;
    }
    return this.song.durationSec / this.rate;
  }

  // File seconds into the verse: what the slider shows, held at the end through the rest
  positionSec(): number {
    return Math.min(this.song?.durationSec ?? 0, this.offsetInVerseNow());
  }

  // The verse for the OS's Now Playing slider (issue #4049), in audio seconds at the current rate:
  // its length and how far into it the clock is, held at the end through the rest
  nowPlayingPosition(): NowPlayingPosition | undefined {
    const durationSec = this.durationSec;
    if (durationSec === undefined || durationSec <= 0) {
      return;
    }
    return { durationSec, positionSec: Math.min(durationSec, this.positionSec() / this.rate) };
  }

  // The position as a share of the song, 0 to 100
  positionPercent(): number {
    if (!this.song || this.song.durationSec === 0) {
      return 0;
    }
    return (this.positionSec() / this.song.durationSec) * 100;
  }

  play() {
    if (this.playing || !this.song) {
      return;
    }
    this.playing = true;
    this.restartFrom(this.positionWhilePaused, this.sink.now() + this.leadSec, { stopSounding: true });
  }

  // Stops everything, the notes sounding now included, and remembers the position
  pause() {
    if (!this.playing) {
      return;
    }
    this.positionWhilePaused = this.offsetInVerseNow();
    this.playing = false;
    this.cancelScheduled({ stopSounding: true });
  }

  // Moves to `positionSec` file seconds into the verse; while playing, playback carries on from there
  seek(positionSec: number) {
    const duration = this.song?.durationSec ?? 0;
    const position = Math.max(0, Math.min(duration, positionSec));
    if (!this.playing) {
      this.positionWhilePaused = position;
      return;
    }
    this.restartFrom(position, this.sink.now() + this.leadSec, { stopSounding: true });
  }

  // The tempo over the file's tempo. The position is kept: what is still to come is re-scheduled
  // at the new rate from the clock's reading now, and the notes sounding play on.
  setRate(rate: number) {
    if (!(rate > 0) || rate === this.rate) {
      return;
    }
    if (!this.playing || this.hasEnded(this.sink.now())) {
      this.rate = rate; // for the next play; a song that has ended is not restarted
      return;
    }
    const anchorSec = this.onOrAfterAnchor(this.sink.now());
    const position = this.offsetInVerseNow();
    this.rate = rate;
    this.restartFrom(position, anchorSec, { stopSounding: false });
  }

  setLoop(loop: boolean) {
    if (loop === this.loop) {
      return;
    }
    this.loop = loop;
    if (!this.playing) {
      return;
    }
    // Re-schedule what lies ahead under the new rule. Called from onVerseEnd, the clock may read a
    // hair short of the boundary just announced; judged a little ahead, it is the new verse. A song
    // that has ended stays ended: the flag is for the next play, exactly as the audio element's was.
    const anchorSec = this.onOrAfterAnchor(this.sink.now() + boundaryEpsilonSec);
    if (this.hasEnded(anchorSec)) {
      return;
    }
    this.restartFrom(this.offsetInVerse(this.timelineAt(anchorSec)), anchorSec, { stopSounding: false });
  }

  // Drops every note not yet started and schedules it afresh: for the player when what a note
  // sounds like has changed (a transposition), since the sink reads that as it schedules
  reschedule() {
    if (!this.playing || this.hasEnded(this.sink.now())) {
      return;
    }
    const anchorSec = this.onOrAfterAnchor(this.sink.now());
    this.restartFrom(this.offsetInVerseNow(), anchorSec, { stopSounding: false });
  }

  // Schedules everything up to the lookahead; cheap when nothing is to be added. The sink's wake
  // calls it as the scheduled stretch runs down, and the player from its backstop timer.
  fill() {
    const song = this.song;
    if (!this.playing || !song || this.endTimelineSec !== undefined) {
      return;
    }
    const now = this.sink.now();
    this.forgetFinished(now);
    if (song.durationSec <= 0) {
      // nothing to play: the verse ends where it begins
      this.endTimelineSec = this.cursorVerse * this.verseLengthSec();
      this.scheduleVerseEnd(this.audioAt(this.endTimelineSec));
      return;
    }
    const horizonTimelineSec = this.timelineAt(now + this.lookaheadSec);
    while (this.cursorTimelineSec() < horizonTimelineSec) {
      const verseStartTimelineSec = this.cursorVerse * this.verseLengthSec();
      if (this.cursorOffsetSec < song.durationSec) {
        const limitOffsetSec = Math.min(song.durationSec, horizonTimelineSec - verseStartTimelineSec);
        const notes = song.notes;
        while (this.cursorNoteIndex < notes.length && notes[this.cursorNoteIndex].startSec < limitOffsetSec) {
          const note = notes[this.cursorNoteIndex];
          this.cursorNoteIndex += 1;
          if (note.startSec < this.cursorOffsetSec) {
            continue; // began before the stretch being scheduled (a seek into the middle of it)
          }
          this.scheduleNote(note, verseStartTimelineSec + note.startSec);
        }
        this.cursorOffsetSec = limitOffsetSec;
        if (limitOffsetSec < song.durationSec) {
          break; // the horizon lies inside this verse's notes
        }
      }
      // the verse's notes end inside the horizon
      if (!this.loop) {
        // the last verse ends with its notes, or at once when playback began in the rest
        this.endTimelineSec = verseStartTimelineSec + this.cursorOffsetSec;
        this.scheduleVerseEnd(this.audioAt(this.endTimelineSec));
        return;
      }
      // the next verse begins after the rest, and that is the boundary announced; the rest has
      // nothing to schedule, so the cursor moves past it at once
      this.scheduleVerseEnd(this.audioAt(verseStartTimelineSec + this.verseLengthSec()));
      this.cursorVerse += 1;
      this.cursorOffsetSec = 0;
      this.cursorNoteIndex = 0;
    }
    // wake when the scheduled stretch has run down to the refill point; the wake already armed for
    // this cursor stands
    const cursorSec = this.cursorTimelineSec();
    if (this.cancelRefillWake && this.refillWakeCursorSec === cursorSec) {
      return;
    }
    this.cancelRefillWake?.();
    const wakeSec = this.audioAt(cursorSec) - this.refillSec;
    this.refillWakeCursorSec = cursorSec;
    this.cancelRefillWake = this.sink.at(Math.max(wakeSec, now), () => {
      this.cancelRefillWake = undefined;
      this.refillWakeCursorSec = undefined;
      this.fill();
    });
  }

  // ---- internals ---------------------------------------------------------------------------

  private timelineAt(audioSec: number): number {
    return this.anchorTimelineSec + (audioSec - this.anchorAudioSec) * this.rate;
  }

  // Playback is anchored a lead ahead of the clock (play, seek): until the clock reaches the
  // anchor the position is the anchor's, and a change made in that moment re-anchors there too
  private onOrAfterAnchor(audioSec: number): number {
    return Math.max(audioSec, this.anchorAudioSec);
  }

  private audioAt(timelineSec: number): number {
    return this.anchorAudioSec + (timelineSec - this.anchorTimelineSec) / this.rate;
  }

  // The rest between verses in file seconds: beats as the song is counted, at the tempo it ends in
  private verseGapSec(): number {
    const song = this.song;
    if (!song || song.durationSec <= 0 || !(song.endTempo > 0)) {
      return 0;
    }
    return (this.verseGapBeats * song.beatQuarterNotes * 60) / song.endTempo;
  }

  // A verse on the timeline: its notes, then the rest
  private verseLengthSec(): number {
    return (this.song?.durationSec ?? 0) + this.verseGapSec();
  }

  // File seconds into the verse at `timelineSec`; past the verse's notes in the rest
  private offsetInVerse(timelineSec: number): number {
    const length = this.verseLengthSec();
    if (length <= 0) {
      return 0;
    }
    return timelineSec - Math.floor(timelineSec / length) * length;
  }

  // File seconds into the verse now, the rest included: where a restart (a pause, a tempo change)
  // carries on from, so it neither cuts the rest short nor starts it again
  private offsetInVerseNow(): number {
    if (!this.playing || !this.song) {
      return this.positionWhilePaused;
    }
    const now = this.sink.now() + (this.announcingVerseEnd ? boundaryEpsilonSec : 0);
    if (this.hasEnded(now)) {
      return this.song.durationSec;
    }
    return this.offsetInVerse(this.timelineAt(this.onOrAfterAnchor(now)));
  }

  private cursorTimelineSec(): number {
    return this.cursorVerse * this.verseLengthSec() + this.cursorOffsetSec;
  }

  // Playback from `positionSec` into the verse, anchored at `audioSec` on the clock: the timeline
  // restarts at verse 0 of this anchor. Notes not yet started are dropped (and the sounding ones
  // too when `stopSounding`), then the stretch ahead is scheduled afresh. From the very end of the
  // verse (a seek to 100%, a play from a pause at the end) the end is announced at once, and the
  // next verse follows when the loop is on, as the audio element's 'ended' and loop did.
  private restartFrom(positionSec: number, audioSec: number, { stopSounding }: { stopSounding: boolean }) {
    this.cancelScheduled({ stopSounding, fromSec: audioSec });
    this.anchorAudioSec = audioSec;
    this.anchorTimelineSec = positionSec;
    this.cursorVerse = 0;
    this.cursorOffsetSec = positionSec;
    this.cursorNoteIndex = 0;
    this.endTimelineSec = undefined;
    this.fill();
  }

  // Whether the timeline has ended (loop off, the last verse over) by `audioSec` on the clock
  private hasEnded(audioSec: number): boolean {
    return this.endTimelineSec !== undefined && this.timelineAt(audioSec) >= this.endTimelineSec;
  }

  // A note that ends with the verse is held through the rest that follows it (when there is one:
  // the last verse has none) with `holdFinalNotes`
  private noteLengthSec(note: MidiNote): number {
    const song = this.song;
    if (!this.holdFinalNotes || !this.loop || !song) {
      return note.durationSec;
    }
    const endsWithVerse = note.startSec + note.durationSec >= song.durationSec - verseEndEpsilonSec;
    return endsWithVerse ? note.durationSec + this.verseGapSec() : note.durationSec;
  }

  private scheduleNote(note: MidiNote, timelineSec: number) {
    const startSec = this.audioAt(timelineSec);
    const durationSec = this.noteLengthSec(note) / this.rate;
    const handle = this.sink.play(note, startSec, durationSec);
    this.scheduled.push({ handle, startSec, endSec: startSec + durationSec });
  }

  private scheduleVerseEnd(audioSec: number) {
    this.cancelWakes.push(
      this.sink.at(audioSec, () => {
        this.announcingVerseEnd = true;
        try {
          this.onVerseEnd?.();
        } finally {
          this.announcingVerseEnd = false;
        }
      }),
    );
  }

  // Every note starting after `fromSec` is cancelled; the ones started by then only when asked.
  // Every wake and verse-end callback is cancelled: the next fill schedules what is still due.
  private cancelScheduled({ stopSounding, fromSec }: { stopSounding: boolean; fromSec?: number }) {
    const now = this.sink.now();
    const cutoffSec = fromSec ?? now;
    const kept: ScheduledEntry[] = [];
    for (const entry of this.scheduled) {
      if (stopSounding || entry.startSec >= cutoffSec) {
        entry.handle.cancel();
      } else if (entry.endSec > now) {
        kept.push(entry);
      }
    }
    this.scheduled = kept;
    const cancels = this.cancelWakes;
    this.cancelWakes = [];
    for (const cancel of cancels) {
      cancel();
    }
    this.cancelRefillWake?.();
    this.cancelRefillWake = undefined;
    this.refillWakeCursorSec = undefined;
  }

  private forgetFinished(now: number) {
    this.scheduled = this.scheduled.filter((entry) => entry.endSec > now);
  }
}
